import type { SearchSettingValue } from '../services/UserSettingsService';

export type SearchType = 'simple' | 'simpleCustom' | 'advanced';
export type SimpleSearchType = Extract<SearchType, 'simple' | 'simpleCustom'>;

export function normalizeSimpleSearchType(raw: unknown): SimpleSearchType {
    return raw === 'simpleCustom' ? 'simpleCustom' : 'simple';
}

export function getRestoredSimpleState(setting: SearchSettingValue | null | undefined): {
    simpleSearchType: SimpleSearchType;
    query: string;
    expressionDialectOmitted: boolean;
    searchIn: string[] | undefined;
} {
    const simpleSearchType = normalizeSimpleSearchType(setting?.lastSearchType);
    const query = (simpleSearchType === 'simpleCustom' ? (setting?.simpleCustom ?? '') : (setting?.simple ?? '')).trim();
    const expressionDialectOmitted = setting?.expression ?? false;
    const searchIn = (setting?.searchIn && setting.searchIn.length > 0) ? setting.searchIn : undefined;
    return { simpleSearchType, query, expressionDialectOmitted, searchIn };
}

export function getRestoredAdvancedQueryState(setting: SearchSettingValue | null | undefined): {
    expressionQuery: string;
    freetextQuery: string;
    filter: string;
    searchIn: string[] | undefined;
    combined: boolean;
} {
    return {
        expressionQuery: (setting?.expressionQuery ?? '').trim(),
        freetextQuery: (setting?.freetextQuery ?? '').trim(),
        filter: (setting?.adv ?? '').trim(),
        searchIn: (setting?.searchIn && setting.searchIn.length > 0) ? setting.searchIn : undefined,
        combined: setting?.combined === true
    };
}

export function getSearchInToStore(attrs: string[] | undefined): string[] | undefined {
    if (!attrs || attrs.length === 0) return undefined;
    const shouldStore = attrs.some((a) => (a || '').toLowerCase() !== 'displayname');
    return shouldStore ? attrs : undefined;
}

export function buildSimpleSearchUpdates(args: {
    simpleSearchType: SimpleSearchType;
    query: string;
    expressionDialectOmitted: boolean;
    searchIn: string[] | undefined;
}): SearchSettingValue {
    const q = (args.query || '').trim();
    if (args.simpleSearchType === 'simpleCustom') {
        return { simpleCustom: q, lastSearchType: 'simpleCustom', expression: args.expressionDialectOmitted, searchIn: args.searchIn };
    }
    return { simple: q, lastSearchType: 'simple', expression: args.expressionDialectOmitted, searchIn: args.searchIn };
}

export function buildAdvancedSearchUpdates(args: { filter: string | undefined; lastSearchType?: SearchType }): SearchSettingValue {
    return { adv: args.filter || undefined, lastSearchType: args.lastSearchType ?? 'advanced' };
}

export function buildExplicitSimpleSearchUpdates(args: {
    dialect: 'expression' | 'freetext';
    query: string;
    searchIn: string[] | undefined;
}): SearchSettingValue {
    const q = (args.query || '').trim();
    return {
        expressionQuery: args.dialect === 'expression' ? q : undefined,
        freetextQuery: args.dialect === 'freetext' ? q : undefined,
        searchIn: args.searchIn
    };
}

